import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Parses a CSS gradient string into structured gradient state:
 * { type: 'linear' | 'radial', angle: number, stops: Array<{ color, position, opacity }> }
 */
export function parseGradientString(gradientStr) {
  if (!gradientStr || typeof gradientStr !== 'string') return null;
  const str = gradientStr.trim();
  const isRadial = str.startsWith('radial-gradient');
  const isLinear = str.startsWith('linear-gradient');
  if (!isRadial && !isLinear) return null;

  const type = isRadial ? 'radial' : 'linear';
  let angle = 90;

  const innerMatch = str.match(/^[a-z-]+\((.*)\)$/is);
  if (!innerMatch) return null;
  const inner = innerMatch[1].trim();

  // Split parts by comma, taking care not to split inside rgba(...) or rgb(...)
  const parts = [];
  let current = '';
  let parenDepth = 0;
  for (let i = 0; i < inner.length; i++) {
    const char = inner[i];
    if (char === '(') parenDepth++;
    else if (char === ')') parenDepth--;
    if (char === ',' && parenDepth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current.trim());

  let stopParts = parts;
  if (isLinear && parts.length > 0) {
    const first = parts[0].toLowerCase();
    const degMatch = first.match(/(-?\d+(?:\.\d+)?)deg/);
    if (degMatch) {
      angle = ((parseInt(degMatch[1], 10) % 360) + 360) % 360;
      stopParts = parts.slice(1);
    } else if (first.includes('to bottom')) {
      angle = 180;
      stopParts = parts.slice(1);
    } else if (first.includes('to right')) {
      angle = 90;
      stopParts = parts.slice(1);
    } else if (first.includes('to top')) {
      angle = 0;
      stopParts = parts.slice(1);
    } else if (first.includes('to left')) {
      angle = 270;
      stopParts = parts.slice(1);
    }
  } else if (isRadial && parts.length > 0 && (parts[0].includes('circle') || parts[0].includes('ellipse'))) {
    stopParts = parts.slice(1);
  }

  const stops = [];
  stopParts.forEach((part, idx) => {
    const posMatch = part.match(/(\d+(?:\.\d+)?)%\s*$/);
    const position = posMatch ? Math.round(parseFloat(posMatch[1])) : Math.round((idx / Math.max(1, stopParts.length - 1)) * 100);
    const colorPart = posMatch ? part.slice(0, posMatch.index).trim() : part.trim();

    let color = '#ffffff';
    let opacity = 1;

    const rgbaMatch = colorPart.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/i);
    const rgbMatch = colorPart.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    const hexMatch = colorPart.match(/#[0-9a-fA-F]{3,8}/);

    if (rgbaMatch) {
      const r = Math.min(255, parseInt(rgbaMatch[1], 10)).toString(16).padStart(2, '0');
      const g = Math.min(255, parseInt(rgbaMatch[2], 10)).toString(16).padStart(2, '0');
      const b = Math.min(255, parseInt(rgbaMatch[3], 10)).toString(16).padStart(2, '0');
      color = `#${r}${g}${b}`;
      opacity = parseFloat(rgbaMatch[4]);
    } else if (rgbMatch) {
      const r = Math.min(255, parseInt(rgbMatch[1], 10)).toString(16).padStart(2, '0');
      const g = Math.min(255, parseInt(rgbMatch[2], 10)).toString(16).padStart(2, '0');
      const b = Math.min(255, parseInt(rgbMatch[3], 10)).toString(16).padStart(2, '0');
      color = `#${r}${g}${b}`;
      opacity = 1;
    } else if (hexMatch) {
      color = hexMatch[0];
      opacity = 1;
    }

    stops.push({ color, position, opacity });
  });

  if (stops.length < 2) return null;
  return { type, angle, stops };
}

/**
 * Generates a standard CSS gradient string from gradient state
 */
export function generateGradientString(state) {
  if (!state || !state.stops || !Array.isArray(state.stops) || state.stops.length === 0) {
    return 'linear-gradient(90deg, #ffffff 0%, #000000 100%)';
  }
  const sortedStops = [...state.stops].sort((a, b) => a.position - b.position);
  const stopsString = sortedStops.map(stop => {
    const hex = stop.color || '#ffffff';
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    const rgb = match ? {
      r: parseInt(match[1], 16),
      g: parseInt(match[2], 16),
      b: parseInt(match[3], 16)
    } : { r: 255, g: 255, b: 255 };
    const opacity = stop.opacity !== undefined ? stop.opacity : 1;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity}) ${stop.position}%`;
  }).join(', ');

  if (state.type === 'radial') {
    return `radial-gradient(circle, ${stopsString})`;
  } else {
    return `linear-gradient(${state.angle || 90}deg, ${stopsString})`;
  }
}

/**
 * Creates default initial gradient from a solid hex color
 */
export function createDefaultGradient(baseHex) {
  let hex = (baseHex || '#1e293b').trim();
  if (!hex.startsWith('#')) hex = '#1e293b';
  
  return {
    type: 'linear',
    angle: 135,
    stops: [
      { color: hex, position: 0, opacity: 1 },
      { color: '#ffffff', position: 100, opacity: 0.85 }
    ]
  };
}

const DEFAULT_PRESETS = [
  { type: 'linear', angle: 135, stops: [{ color: '#6366f1', position: 0, opacity: 1 }, { color: '#a855f7', position: 100, opacity: 1 }] },
  { type: 'linear', angle: 135, stops: [{ color: '#00c6ff', position: 0, opacity: 1 }, { color: '#0072ff', position: 100, opacity: 1 }] },
  { type: 'linear', angle: 135, stops: [{ color: '#10b981', position: 0, opacity: 1 }, { color: '#059669', position: 100, opacity: 1 }] },
  { type: 'linear', angle: 135, stops: [{ color: '#f59e0b', position: 0, opacity: 1 }, { color: '#ef4444', position: 100, opacity: 1 }] },
  { type: 'linear', angle: 135, stops: [{ color: '#1b365d', position: 0, opacity: 1 }, { color: '#d4af37', position: 100, opacity: 1 }] },
  { type: 'linear', angle: 135, stops: [{ color: '#1f2937', position: 0, opacity: 1 }, { color: '#111827', position: 100, opacity: 1 }] },
  { type: 'linear', angle: 90, stops: [{ color: '#bdb2ff', position: 0, opacity: 1 }, { color: '#a0c4ff', position: 100, opacity: 1 }] },
  { type: 'radial', angle: 90, stops: [{ color: '#ffffff', position: 0, opacity: 1 }, { color: '#f1f5f9', position: 100, opacity: 1 }] }
];

export default function GradientModal({
  isOpen,
  onClose,
  value,
  onApply,
  title = "Gradient Editor"
}) {
  const [gradientState, setGradientState] = useState({
    type: 'linear',
    angle: 90,
    stops: [
      { color: '#bdb2ff', position: 0, opacity: 1 },
      { color: '#a0c4ff', position: 100, opacity: 1 }
    ]
  });
  const [selectedStopIndex, setSelectedStopIndex] = useState(0);
  const [windowPos, setWindowPos] = useState({ x: 220, y: 90 });

  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const barRef = useRef(null);

  const [presets, setPresets] = useState(() => {
    try {
      const saved = localStorage.getItem('tc_gradient_presets');
      return saved ? JSON.parse(saved) : DEFAULT_PRESETS;
    } catch (e) {
      return DEFAULT_PRESETS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('tc_gradient_presets', JSON.stringify(presets));
    } catch (e) {
      // ignore storage errors
    }
  }, [presets]);

  // When modal opens, sync value
  useEffect(() => {
    if (isOpen) {
      const parsed = parseGradientString(value);
      if (parsed) {
        setGradientState(parsed);
        setSelectedStopIndex(0);
      } else if (value && typeof value === 'string' && value.startsWith('#')) {
        setGradientState(createDefaultGradient(value));
        setSelectedStopIndex(0);
      } else {
        setGradientState({
          type: 'linear',
          angle: 135,
          stops: [
            { color: '#bdb2ff', position: 0, opacity: 1 },
            { color: '#a0c4ff', position: 100, opacity: 1 }
          ]
        });
        setSelectedStopIndex(0);
      }
    }
  }, [isOpen, value]);

  // Draggable window mechanics
  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX - windowPos.x,
      y: e.clientY - windowPos.y
    };

    const handleMouseMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      setWindowPos({
        x: Math.max(10, Math.min(window.innerWidth - 440, moveEvent.clientX - dragStartRef.current.x)),
        y: Math.max(10, Math.min(window.innerHeight - 300, moveEvent.clientY - dragStartRef.current.y))
      });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleInsertStop = () => {
    const activeStop = gradientState.stops[selectedStopIndex] || gradientState.stops[0];
    if (!activeStop) return;
    const currentPos = activeStop.position;
    const positions = gradientState.stops.map(s => s.position).sort((a, b) => a - b);
    const nextPos = positions.find(p => p > currentPos) || 100;
    const newPos = Math.round((currentPos + nextPos) / 2);

    const newStop = {
      color: activeStop.color || '#ffffff',
      position: newPos,
      opacity: activeStop.opacity !== undefined ? activeStop.opacity : 1
    };

    setGradientState(prev => {
      const newStops = [...prev.stops, newStop];
      newStops.sort((a, b) => a.position - b.position);
      const idx = newStops.indexOf(newStop);
      setSelectedStopIndex(idx >= 0 ? idx : newStops.length - 1);
      return { ...prev, stops: newStops };
    });
  };

  const handleDeleteStop = () => {
    if (gradientState.stops.length <= 2) return;
    setGradientState(prev => {
      const newStops = prev.stops.filter((_, idx) => idx !== selectedStopIndex);
      setSelectedStopIndex(0);
      return { ...prev, stops: newStops };
    });
  };

  const handleReverseGradient = () => {
    setGradientState(prev => {
      const reversedStops = prev.stops.map(s => ({
        ...s,
        position: 100 - s.position
      }));
      reversedStops.sort((a, b) => a.position - b.position);
      return { ...prev, stops: reversedStops };
    });
    setSelectedStopIndex(0);
  };

  const handleCopyStop = () => {
    const activeStop = gradientState.stops[selectedStopIndex] || gradientState.stops[0];
    if (!activeStop) return;
    const newPos = Math.min(100, activeStop.position + 5);

    const newStop = {
      color: activeStop.color || '#ffffff',
      position: newPos,
      opacity: activeStop.opacity !== undefined ? activeStop.opacity : 1
    };

    setGradientState(prev => {
      const newStops = [...prev.stops, newStop];
      newStops.sort((a, b) => a.position - b.position);
      const idx = newStops.indexOf(newStop);
      setSelectedStopIndex(idx >= 0 ? idx : newStops.length - 1);
      return { ...prev, stops: newStops };
    });
  };

  const handleApply = () => {
    const cssString = generateGradientString(gradientState);
    if (onApply) onApply(cssString);
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  const activeStop = gradientState.stops[selectedStopIndex] || gradientState.stops[0];
  const currentGradientCss = generateGradientString(gradientState);

  return createPortal(
    <div style={{
      position: 'fixed',
      top: `${windowPos.y}px`,
      left: `${windowPos.x}px`,
      width: '420px',
      background: 'var(--card-bg, #1b1b1d)',
      border: '1px solid var(--border-color, #3c3c42)',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 12px 36px rgba(0,0,0,0.65)',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      zIndex: 100005,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: 'var(--text-main, #dfdfe3)'
    }}>
      {/* Header (Draggable) */}
      <div 
        onMouseDown={handleDragStart}
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          borderBottom: '1px solid var(--border-color, #2d2d30)', 
          paddingBottom: '8px',
          cursor: 'grab',
          userSelect: 'none'
        }}
      >
        <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </span>
        <button 
          type="button"
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '15px' }}
        >
          ✕
        </button>
      </div>

      {/* Gradient Preview Bar */}
      <div style={{ padding: '0 8px' }}>
        <div style={{ position: 'relative', margin: '18px 0 28px 0' }}>
          <div 
            ref={barRef}
            onClick={(e) => {
              if (e.target !== e.currentTarget) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percent = Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
              
              const defaultColor = activeStop ? activeStop.color : '#ffffff';
              const defaultOpacity = activeStop && activeStop.opacity !== undefined ? activeStop.opacity : 1;
              const newStop = { color: defaultColor, position: percent, opacity: defaultOpacity };
              
              setGradientState(prev => {
                const newStops = [...prev.stops, newStop];
                newStops.sort((a, b) => a.position - b.position);
                const idx = newStops.indexOf(newStop);
                setSelectedStopIndex(idx >= 0 ? idx : newStops.length - 1);
                return { ...prev, stops: newStops };
              });
            }}
            style={{
              width: '100%',
              height: '34px',
              borderRadius: '6px',
              border: '1px solid #3c3c42',
              background: currentGradientCss,
              cursor: 'crosshair',
              position: 'relative'
            }}
            title="Click bar to add color stop"
          />
          
          {/* Center alignment guide line */}
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '17px',
            height: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
            pointerEvents: 'none'
          }} />
          
          {/* Handle circles for each stop */}
          {gradientState.stops.map((stop, index) => {
            const isSelected = index === selectedStopIndex;
            return (
              <div
                key={index}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelectedStopIndex(index);
                  const startX = e.clientX;
                  const startPos = stop.position;
                  
                  const handleMouseMove = (moveEvent) => {
                    if (!barRef.current) return;
                    const rect = barRef.current.getBoundingClientRect();
                    const deltaX = moveEvent.clientX - startX;
                    const deltaPercent = Math.round((deltaX / rect.width) * 100);
                    const newPercent = Math.max(0, Math.min(100, startPos + deltaPercent));
                    
                    setGradientState(prev => {
                      const newStops = [...prev.stops];
                      newStops[index] = { ...newStops[index], position: newPercent };
                      return { ...prev, stops: newStops };
                    });
                  };
                  
                  const handleMouseUp = () => {
                    window.removeEventListener('mousemove', handleMouseMove);
                    window.removeEventListener('mouseup', handleMouseUp);
                  };
                  
                  window.addEventListener('mousemove', handleMouseMove);
                  window.addEventListener('mouseup', handleMouseUp);
                }}
                style={{
                  position: 'absolute',
                  left: `calc(${stop.position}% - 8px)`,
                  top: '9px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: stop.color,
                  border: isSelected ? '2px solid #1677ff' : '2px solid #ffffff',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.6)',
                  cursor: 'ew-resize',
                  zIndex: isSelected ? 10 : 2
                }}
                title={`Stop ${index + 1}: ${stop.color} (${stop.position}%)`}
              />
            );
          })}
        </div>
      </div>

      {/* Mode & Angle */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '11px', color: '#8e8e93' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span>Type</span>
          <select
            value={gradientState.type}
            onChange={(e) => setGradientState({ ...gradientState, type: e.target.value })}
            style={{ backgroundColor: '#232326', border: '1px solid #3c3c42', borderRadius: '4px', padding: '5px', color: '#fff', fontSize: '11px', outline: 'none' }}
          >
            <option value="linear">Linear</option>
            <option value="radial">Radial</option>
          </select>
        </div>

        {gradientState.type === 'linear' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Angle</span>
              <span style={{ color: '#fff' }}>{gradientState.angle || 90}°</span>
            </div>
            <input
              type="range"
              min="0"
              max="360"
              value={gradientState.angle || 90}
              onChange={(e) => setGradientState({ ...gradientState, angle: parseInt(e.target.value, 10) || 0 })}
              style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer', marginTop: '6px' }}
            />
          </div>
        )}
      </div>

      {/* Selected Stop Parameters */}
      {activeStop && (
        <div style={{ borderTop: '1px solid #2d2d30', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>
            Selected Stop #{selectedStopIndex + 1}
          </span>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '11px', color: '#8e8e93' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Position</span>
                <span style={{ color: '#fff' }}>{activeStop.position}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={activeStop.position}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10) || 0;
                  setGradientState(prev => {
                    const newStops = [...prev.stops];
                    newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], position: val };
                    return { ...prev, stops: newStops };
                  });
                }}
                style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer', marginTop: '6px' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Opacity</span>
                <span style={{ color: '#fff' }}>{Math.round((activeStop.opacity !== undefined ? activeStop.opacity : 1) * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round((activeStop.opacity !== undefined ? activeStop.opacity : 1) * 100)}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) / 100;
                  setGradientState(prev => {
                    const newStops = [...prev.stops];
                    newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], opacity: val };
                    return { ...prev, stops: newStops };
                  });
                }}
                style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer', marginTop: '6px' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: '#8e8e93', marginTop: '4px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <span>Color</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={activeStop.color}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGradientState(prev => {
                      const newStops = [...prev.stops];
                      newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color: val };
                      return { ...prev, stops: newStops };
                    });
                  }}
                  style={{ flex: 1, backgroundColor: '#232326', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px 6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                />
                <input
                  type="color"
                  value={activeStop.color?.startsWith('#') && activeStop.color.length === 7 ? activeStop.color : '#ffffff'}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGradientState(prev => {
                      const newStops = [...prev.stops];
                      newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color: val };
                      return { ...prev, stops: newStops };
                    });
                  }}
                  style={{ width: '28px', height: '24px', border: 'none', padding: 0, cursor: 'pointer', backgroundColor: 'transparent' }}
                />
                {typeof window !== 'undefined' && 'EyeDropper' in window && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const eyeDropper = new window.EyeDropper();
                        const result = await eyeDropper.open();
                        const val = result.sRGBHex;
                        setGradientState(prev => {
                          const newStops = [...prev.stops];
                          newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color: val };
                          return { ...prev, stops: newStops };
                        });
                      } catch (err) {
                        // EyeDropper cancelled or unsupported
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      backgroundColor: '#232326',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#dfdfe3',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    title="Sample color from screen"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 3L11 13" />
                      <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
                      <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ borderTop: '1px solid #2d2d30', paddingTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleInsertStop}
          style={{ flex: 1, padding: '6px 0', fontSize: '10px', fontWeight: 'bold', border: '1px solid #3c3c42', borderRadius: '4px', backgroundColor: '#232326', color: '#ffffff', cursor: 'pointer' }}
        >
          Insert Stop
        </button>
        <button
          type="button"
          onClick={handleCopyStop}
          style={{ flex: 1, padding: '6px 0', fontSize: '10px', fontWeight: 'bold', border: '1px solid #3c3c42', borderRadius: '4px', backgroundColor: '#232326', color: '#ffffff', cursor: 'pointer' }}
        >
          Copy
        </button>
        <button
          type="button"
          onClick={handleDeleteStop}
          disabled={gradientState.stops.length <= 2}
          style={{
            flex: 1,
            padding: '6px 0',
            fontSize: '10px',
            fontWeight: 'bold',
            border: '1px solid #3c3c42',
            borderRadius: '4px',
            backgroundColor: gradientState.stops.length <= 2 ? '#1b1b1d' : '#232326',
            color: gradientState.stops.length <= 2 ? '#55555c' : '#ffffff',
            cursor: gradientState.stops.length <= 2 ? 'default' : 'pointer'
          }}
        >
          Delete
        </button>
        <button
          type="button"
          onClick={handleReverseGradient}
          style={{ flex: 1, padding: '6px 0', fontSize: '10px', fontWeight: 'bold', border: '1px solid #3c3c42', borderRadius: '4px', backgroundColor: '#232326', color: '#ffffff', cursor: 'pointer' }}
        >
          Reverse
        </button>
      </div>

      {/* Presets Row */}
      <div style={{ borderTop: '1px solid #2d2d30', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>Presets</span>
          <button
            type="button"
            onClick={() => {
              const currentStr = JSON.stringify(gradientState);
              if (!presets.some(p => JSON.stringify(p) === currentStr)) {
                setPresets([...presets, { ...gradientState }]);
              }
            }}
            style={{ backgroundColor: 'transparent', border: 'none', color: '#1677ff', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', padding: '2px 4px', borderRadius: '3px' }}
          >
            + Save Current
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', maxHeight: '72px', overflowY: 'auto', padding: '2px' }}>
          {presets.map((preset, idx) => {
            const gradStr = generateGradientString(preset);
            return (
              <div style={{ position: 'relative' }} key={idx}>
                <div
                  onClick={() => {
                    setGradientState({ ...preset });
                    setSelectedStopIndex(0);
                  }}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '4px',
                    border: '1px solid #3c3c42',
                    background: gradStr,
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }}
                  title="Apply preset"
                />
                {idx >= DEFAULT_PRESETS.length && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPresets(presets.filter((_, i) => i !== idx));
                    }}
                    style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#3c3c42',
                      border: 'none',
                      color: '#dfdfe3',
                      fontSize: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    title="Delete Preset"
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid var(--border-color, #2d2d30)', paddingTop: '10px', marginTop: '4px' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '6px 14px',
            fontSize: '11px',
            background: 'var(--panel-bg, #232326)',
            color: 'var(--text-main, #dfdfe3)',
            border: '1px solid var(--border-color, #3c3c42)',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          style={{
            padding: '6px 16px',
            fontSize: '11px',
            fontWeight: '600',
            background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
            color: '#ffffff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          OK
        </button>
      </div>
    </div>,
    document.body
  );
}
